import { i18n } from '@/i18n/settings'
import * as Timezone from '@/services/Timezone'

/**
 * Dates and times, written in the timezone of the visitor (see services/Timezone).
 *
 * The server sends every instant in UTC; a page shows them in the timezone the visitor is reading
 * from — the one they picked, the one the browser declares, or the site's own one, which is also the
 * timezone the event windows are measured in (Europe/Rome by default, config/events.php). The day is
 * read there too: the last day of an event is the last day the players can race, wherever they are.
 *
 * A date is the day, the abbreviated month and the year ("10 Ott 2026"), in the language of the
 * page: the month is a word, and which word it is is the language. Plain `en` puts it in front
 * ("Oct 10, 2026"), so English is written with en-GB.
 */

/** Locale each language writes its dates with (see the note above). */
const DATE_LOCALES = { en: 'en-GB', it: 'it' }

function locale()
{
  return DATE_LOCALES[i18n.global.locale.value] ?? DATE_LOCALES.en
}

/** The options as they are, plus the timezone the visitor is reading from. */
function withTimezone(options)
{
  const timeZone = Timezone.current()

  return timeZone ? { timeZone, ...options } : { ...options }
}

/**
 * One instant written with the options it is asked for — the whole date, or the day alone, or the
 * day and the month (see DateRange).
 */
function writeDate(iso, options)
{
  const text = i18n.global.d(new Date(iso), withTimezone(options), locale())

  // Intl writes Italian months lowercase ("10 ott 2026"); the site starts them with a capital
  // letter, the way "10 Oct 2026" reads and the tables write them.
  return text.replace(/\p{L}+/u, (word) => word[0].toUpperCase() + word.slice(1))
}

/** Date of an event window: "10 Ott 2026". */
export function formatDate(iso)
{
  return iso ? writeDate(iso, { day: 'numeric', month: 'short', year: 'numeric' }) : '–'
}

/** Day on its own ("1"), for the end of a period that shares all the rest (see DateRange). */
export function formatDay(iso)
{
  return iso ? writeDate(iso, { day: 'numeric' }) : '–'
}

/** Day and abbreviated month, without the year ("10 Ott"), for a period inside one year. */
export function formatDayMonth(iso)
{
  return iso ? writeDate(iso, { day: 'numeric', month: 'short' }) : '–'
}

/** Time of day of a deadline: "23:59". */
export function formatTime(iso)
{
  return iso ? i18n.global.d(new Date(iso), withTimezone({ hour: '2-digit', minute: '2-digit', hour12: false }), locale()) : '–'
}

/**
 * Date and time of an instant: a race is a moment, not a day, and the players have to be able to
 * compare it with the event windows and with each other.
 */
export function formatDateTime(iso)
{
  return iso ? `${formatDate(iso)} ${formatTime(iso)}` : '–'
}

/**
 * Year, month (1 = January) and day an instant falls on, read in the timezone of the visitor: it is
 * what a period writes once when its two ends have it in common (see DateRange).
 */
export function dateParts(iso)
{
  const parts = new Intl.DateTimeFormat('en-US', withTimezone({ year: 'numeric', month: 'numeric', day: 'numeric' }))
    .formatToParts(new Date(iso))
  const value = (type) => Number(parts.find((part) => part.type === type)?.value)

  return { year: value('year'), month: value('month'), day: value('day') }
}

/**
 * Name of the month a date falls in, in the language of the page: it is a word of the event's title
 * ("Championship of September"), not a date, so it follows the page and not the date format.
 */
export function getMonthName(iso)
{
  return iso ? i18n.global.d(new Date(iso), withTimezone({ month: 'long' }), locale()) : '–'
}
